import React, { useState, useRef, useEffect, useMemo } from "react";

import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";
import { StyleSheet, css } from "aphrodite";

import "../../assets/styles/muiComponents.css";
import { Block, CheckCircle, ContentCopy, Delete, Edit, RemoveRedEye } from "@mui/icons-material";

type ColOverride = Partial<{
  width: number,
  minWidth: number,
  flex: number,
}>;

type Props = {
  head: any[],
  row: any[],
  totals?: any,
  loading?: boolean,
  hasTotal?: boolean,
  onEyeClick?: (row: any) => void,
  onEditClick?: (row: any) => void,
  onCloneClick?: (row: any) => void,
  onDeleteClick?: (row: any) => void,
  columnOverrides?: Record<string, ColOverride>,
};

function CustomDatagrid(props) {
  const { head, row, totals, onEyeClick, onEditClick, onCloneClick, onDeleteClick, columnOverrides = {} } = props;

  // NÃO muta o props.head direto
  const setHeaders = (head || []).map((sr) => ({ ...sr }));
  const setRows = row;

  let setTotals = [];
  setTotals.push(props.totals);

  const topGridContainerRef = useRef(null);
  const bottomGridContainerRef = useRef(null);

  const numberComparator = (v1, v2) =>
    String(v1).replace(".", "").replace(",", ".") -
    String(v2).replace(".", "").replace(",", ".");

  // aplica sortComparator na cópia (setHeaders)
  setHeaders.forEach((sr) => {
    if (sr.isNumber) {
      sr.sortComparator = numberComparator;
    } else {
      sr.isNumber = false;
    }
  });

  const columns = useMemo(() => {
  const cols = (setHeaders || []).map((c) => {
    const field = c.field;
    const ov = columnOverrides[field];
    if (!ov) return c;

    const base = { ...c, ...ov };
    if (ov.flex != null) delete base.width;
    return base;
  });

  const hasActions =
    typeof onEyeClick === "function" || typeof onEditClick === "function" || typeof onCloneClick === "function" || typeof onDeleteClick === "function";

  if (hasActions) {
    cols.push({
      field: "__actions",
      headerName: props.actionsHeaderName ?? "Ações",
      width: props.actionsWidth ?? 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      headerClassName: "acoes-header",
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", justifyContent: "center" }}>
          {typeof onEyeClick === "function" && (
            <Tooltip title="Visualizar">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onEyeClick(params.row);
                }}
                sx={{ p: 0.5 }}
              >
                <RemoveRedEye fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {typeof onEditClick === "function" && (
            <Tooltip title="Editar">
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditClick(params.row);
                }}
                sx={{ p: 0.5 }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {typeof onCloneClick === "function" && (
            <Tooltip title="Duplicar">
              <IconButton
                size="small"
                // color="info"
                onClick={(e) => {
                  e.stopPropagation();
                  onCloneClick(params.row);
                }}
                sx={{ p: 0.5 }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
  
          {typeof onDeleteClick === "function" && (
            <Tooltip
              title={
                params.row.content?.situacaoId === "4" || params.row?.inativo === "Cancelado"
                  ? "Ativar" 
                  : "Cancelar" 
              }
            >
              <IconButton
                size="small"
                color={
                  params.row.content?.situacaoId === "4" || params.row?.inativo === "Cancelado"
                    ? "success" 
                    : "error" 
                }
                disabled={params.row.content?.situacaoId === "1"}
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteClick(params.row);
                }}
                sx={{ p: 0.5 }}
              >
                {params.row.content?.situacaoId === "4" || params.row?.inativo === "Cancelado" ? (
                  <CheckCircle fontSize="small" />
                ) : (
                  <Block fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    });
  }

  return cols;
}, [setHeaders, columnOverrides, onEyeClick, onEditClick, onCloneClick, onDeleteClick, props.actionsHeaderName, props.actionsWidth]);


  const columnsTotal = useMemo(
  () =>
    columns.map((col) =>
      col.field === "__actions"
        ? { ...col, headerName: "", renderCell: () => null }
        : col
    ),
  [columns]
);

  function generateRandom() {
    let length = 8,
      charset =
        "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
      retVal = "";
    for (let i = 0, n = charset.length; i < length; ++i) {
      retVal += charset.charAt(Math.floor(Math.random() * n));
    }
    return retVal;
  }

  const syncScroll = (sourceRef, targetRef) => {
    if (sourceRef.current && targetRef.current) {
      const sourceScroll = sourceRef.current.scrollLeft;
      targetRef.current.scrollLeft = sourceScroll;
    }
  };

  useEffect(() => {
    // const timeout = setTimeout(() => {
    //   const topScrollElement = topGridContainerRef.current?.querySelector(
    //     ".MuiDataGrid-virtualScroller"
    //   );
    //   const bottomScrollElement = bottomGridContainerRef.current?.querySelector(
    //     ".MuiDataGrid-virtualScroller"
    //   );

    //   if (topScrollElement && bottomScrollElement) {
    //     const handleTopScroll = () =>
    //       (bottomScrollElement.scrollLeft = topScrollElement.scrollLeft);
    //     const handleBottomScroll = () =>
    //       (topScrollElement.scrollLeft = bottomScrollElement.scrollLeft);

    //     topScrollElement.addEventListener("scroll", handleTopScroll);
    //     bottomScrollElement.addEventListener("scroll", handleBottomScroll);

    //     return () => {
    //       topScrollElement.removeEventListener("scroll", handleTopScroll);
    //       bottomScrollElement.removeEventListener("scroll", handleBottomScroll);
    //     };
    //   }
    // }, 500); // Delay execution

    // return () => clearTimeout(timeout); // Cleanup timeout on unmount
  }, []);

  return (
    <>
      {window.screen.width <= 768 ? (
        <div
          ref={topGridContainerRef}
          style={{
            overflow: "auto",
            // maxHeight: 300
          }}
        >
          <DataGrid
            autoHeight
            disableColumnFilter
            columns={columns}
            columnHeaderHeight={30}
            getRowId={(row) => generateRandom()}
            rows={setRows}
            rowHeight={25}
            isRowSelectable={() => false}
            hideFooter
            disableRowSelectionOnClick
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            loading={props.loading}
            slotProps={{
              noResultsOverlay: {
                style: { color: "#4b4b4b", fontSize: 14 },
              },
              row: {
                style: { color: "#4b4b4b", fontSize: 12 },
              },
            }}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0
                ? "even_row"
                : "odd_row"
            }
            sx={{
              // header da coluna Ações
              "& .acoes-header": {
                backgroundColor: "#669966",
                color: "#fff",
                fontWeight: 700,
              },
              // deixa o texto do título branquinho também
              "& .acoes-header .MuiDataGrid-columnHeaderTitle": {
                color: "#fff",
              },
            }}
          />
        </div>
      ) : (
        <DataGrid
          autoHeight
          disableColumnFilter
          columns={columns}
          columnHeaderHeight={30}
          getRowId={(row) => generateRandom()}
          rows={setRows}
          rowHeight={25}
          isRowSelectable={() => false}
          hideFooter
          disableRowSelectionOnClick
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          loading={props.loading}
          slotProps={{
            noResultsOverlay: {
              style: { color: "#4b4b4b", fontSize: 14 },
            },
            row: {
              style: { color: "#4b4b4b", fontSize: 12 },
            },
          }}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even_row" : "odd_row"
          }
          sx={{
            // header da coluna Ações
            "& .acoes-header": {
              backgroundColor: "#669966",
              color: "#fff",
              fontWeight: 700,
            },
            // deixa o texto do título branquinho também
            "& .acoes-header .MuiDataGrid-columnHeaderTitle": {
              color: "#fff",
            },
          }}
        />
      )}

      {!props.hasTotal ? (
        <>
          <Grid container wrap="wrap">
            <Grid
              item
              xs={12}
              style={{
                backgroundColor: "#669966",
                borderRadius: "3px 3px 0 0",
                display: "flex",
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
                height: 25,
                marginTop: "1rem",
              }}
            >
              <p className={css(styles.head)}>TOTAL GERAL</p>
            </Grid>
          </Grid>

          {window.screen.width <= 768 ? (
            <div
              ref={bottomGridContainerRef}
              style={{
                overflow: "auto",
                // maxHeight: 100
              }}
            >
              <DataGrid
                autoHeight
                columns={columnsTotal}
                columnHeaderHeight={0}
                getRowId={(row) => generateRandom()}
                rows={setTotals}
                rowHeight={25}
                row
                isRowSelectable={() => false}
                hideFooter
                disableRowSelectionOnClick
                localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
                slotProps={{
                  row: {
                    style: {
                      backgroundColor: "#fff",
                      color: "#4b4b4b",
                      fontSize: 12,
                      // fontWeight: "600",
                      // fontFamily: "Poppins",
                    },
                  },
                }}
                sx={{
                  // header da coluna Ações
                  "& .acoes-header": {
                    backgroundColor: "#669966",
                    color: "#fff",
                    fontWeight: 700,
                  },
                  // deixa o texto do título branquinho também
                  "& .acoes-header .MuiDataGrid-columnHeaderTitle": {
                    color: "#fff",
                  },
                }}
              />
            </div>
          ) : (
            <DataGrid
              autoHeight
              columns={columnsTotal}
              columnHeaderHeight={0}
              getRowId={(row) => generateRandom()}
              rows={setTotals}
              rowHeight={25}
              row
              isRowSelectable={() => false}
              hideFooter
              disableRowSelectionOnClick
              localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
              slotProps={{
                row: {
                  style: {
                    backgroundColor: "#fff",
                    color: "#4b4b4b",
                    fontSize: 12,
                    // fontWeight: "600",
                    // fontFamily: "Poppins",
                  },
                },
              }}
              sx={{
                // header da coluna Ações
                "& .acoes-header": {
                  backgroundColor: "#669966",
                  color: "#fff",
                  fontWeight: 700,
                },
                // deixa o texto do título branquinho também
                "& .acoes-header .MuiDataGrid-columnHeaderTitle": {
                  color: "#fff",
                },
              }}
            />
          )}
        </>
      ) : null}
    </>
  );
}

const styles = StyleSheet.create({
  head: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "#fff",
    fontWeight: 600,
    fontSize: 14,
    // fontFamily: "Poppins",
  },
});

export default CustomDatagrid;
